import _ from 'lodash';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { accountGetPortfolioAction } from '../modules/Account/accountActions';
import {
    bonusesSetActiveListSuccess,
    bonusesSetAvailableList,
    setActivePromocodesSuccess,
    setUsedPromocodesAction,
    setWelcomeApplyBonusAction,
} from '../modules/Bonus/bonusesActions';
import { MODALS_LOG_ACTION_TYPES } from '../modules/Modals/Modals';
import { setPartnerParams } from '../modules/Partner/partnerActions';
import {
    addAbTestToIsUsedQueueAction,
    appInitAction,
    appInitFailure,
    appInitSuccess,
    appLog,
    completeOnboardingFailure,
    completeOnboardingSuccess,
    deleteProfileAction,
    deleteProfileFailure,
    deleteProfileSuccess,
    loadAccountTradeInfoAction,
    loadAccountTradeInfoFailure,
    loadAccountTradeInfoSuccess,
    logAction,
    logAppsflyerAction,
    logPaymentOpenAppsflyerAction,
    openModalAction,
    sendFacebookEventAction,
    sendRequestFeedbackAction,
    sendRequestFeedbackFailure,
    sendRequestFeedbackSuccess,
    setAppInitDataAction,
    setLangDataAction,
    setRecentDoneAction,
    setUserSelectedAccountCode,
    updateAbTestIsUsedAction,
    updateLangAction,
    userPromocodeApplyAction,
    userPromocodeApplyFailure,
    userPromocodeApplySuccess,
    userPromocodeRemoveAction,
    userPromocodeRemoveFailure,
    userPromocodeRemoveSuccess,
    userUpdateAction,
    userUpdateFailure,
    userUpdatePasswordAction,
    userUpdatePasswordFailure,
    userUpdateSuccess,
    userUpdateUsernameAction,
    userUpdateUsernameFailure,
    userUpdateUsernameSuccess,
} from './appActions';
import {
    _ROUTER_LOCATION_CHANGE,
    ADD_AB_TEST_TO_IS_USED_QUEUE,
    APP_INIT_START,
    APP_LOG,
    CLOSE_MODAL,
    COMPLETE_ONBOARDING_START,
    DELETE_PROFILE_START,
    LOAD_ACCOUNT_TRADE_INFO_START,
    LOCALE_CODES,
    LOG_ACTION,
    LOG_AF_EVENT_NAMES,
    LOG_APPSFLYER_ACTION,
    LOG_EVENT_TYPES,
    LOG_PAYMENT_OPEN_APPSFLYER_ACTION,
    OPEN_MODAL,
    SEND_FACEBOOK_EVENT_ACTION,
    SEND_REQUEST_FEEDBACK_START,
    SET_RECENT_DONE,
    SET_USER_SELECTED_ACCOUNT_CODE,
    UPDATE_AB_TEST_IS_USED_START,
    UPDATE_LANG_ACTION,
    USER_PROMOCODE_APPLY_START,
    USER_PROMOCODE_REMOVE_START,
    USER_UPDATE_PASSWORD_START,
    USER_UPDATE_START,
    USER_UPDATE_USERNAME_START,
} from './appConstants';
import { appHistoryPushWithSearchParams } from './appHistory';
import {
    getAbTestsIsUsedQueueSelector,
    getAbTestsSelector,
    getAppFeatures,
    getAppLangSelector,
    getAvailableLocales,
    getPasswordMaxLengthSelector,
    getPasswordMinLengthSelector,
    getUserLocale,
    getUserUidSelector,
    makeSelectOpenedModalPrevious,
} from './appSelectors';
import { checkIsPasswordValidWithParams, getSystemLocale, setSystemLocale } from './appUtils';
import { failurePut, IFailurePutCall } from './failurePut';
import { getRequest, postRequest, putRequest } from './request';
import type {
    ILocationChangeAction,
    IOpenModalPayload,
    ISetAppInitDataPayload,
    IUpdateUsernameSuccessPayload,
    IUser,
    TAbTestNames,
    TFeaturesNames,
} from './types/appActions.t';
import { sendNotificationMessageAction } from '../modules/Notifications/notificationsReducer';
import {
    NOTIFICATION_MESSAGE_AB_TESTS_EVENTS,
    NOTIFICATIONS_MESSAGE_TYPES,
} from '../modules/Notifications/notificationsConstants';
import { logoutStartSaga } from '../modules/Login/loginSagas';
import timeout from '../utils/timeout';
import { pwaSlice } from '../modules/Pwa/pwaReducer';
import { alertsSlice } from '../modules/Alerts/alertsReducer';
import { userSlice } from './userReducer';
import { setSelectedAccountSaga } from '../providers/WebSocketProvider/sagas/liteTradeWssEventsSaga';
import sendFacebookEvent from "../utils/sendFacebookEvent";

// import * as Sentry from '@sentry/browser';

export function* appInitStartSaga() {
    try {
        const data: ISetAppInitDataPayload = yield call<(str: string) => Promise<any>>(getRequest, '/init');
        yield put(setAppInitDataAction(data));
        yield call(setSelectedAccountSaga);
        yield call(updateLangSaga, { type: UPDATE_LANG_ACTION, payload: { locale: data.user?.locale } });
        yield put(bonusesSetActiveListSuccess(data.bonusesActive));
        yield put(bonusesSetAvailableList(data.bonusesAvailable));
        yield put(setActivePromocodesSuccess(data.promocodesActive));
        yield put(setUsedPromocodesAction(data.promocodesUsed));
        yield put(setPartnerParams(data.partner || {}));
        yield put(pwaSlice.actions.setIsPwaAllowedAction(data.isPwaAllowed));
        yield put(alertsSlice.actions.initAlertsAction());
        yield put(userSlice.actions.getUserStatusesAction());
        yield put(appInitSuccess());
    } catch (e: any) {
        yield call<IFailurePutCall>(failurePut, e, appInitFailure);
    }
}

export function* appLogSaga({ payload }: ReturnType<typeof appLog>) {
    return;
    yield put(
        sendNotificationMessageAction({
            type: NOTIFICATIONS_MESSAGE_TYPES.APP_LOG,
            params: { event: payload.type, payload: payload.payload },
        }),
    );
}

export function* locationChangeSaga({ payload }: ILocationChangeAction) {
    yield put(
        appLog({
            type: LOG_EVENT_TYPES.page,
            payload: {
                pathname: payload.location.pathname,
                search: payload.location.search,
            },
        }),
    );
}

export function* logActionSaga({ payload }: ReturnType<typeof logAction>) {
    yield put(
        appLog({
            type: LOG_EVENT_TYPES.action,
            payload: payload,
        }),
    );
}

export function* logAppsflyerSaga({ payload }: ReturnType<typeof logAppsflyerAction>) {
    yield put(
        sendNotificationMessageAction({
            type: NOTIFICATIONS_MESSAGE_TYPES.APPSFLYER_LOG,
            params: { event: payload.name, payload: payload.payload },
        }),
    );
}

export function* userUpdateSaga({ payload }: ReturnType<typeof userUpdateAction>) {
    try {
        const data: IUser = yield call<
            (
                str: string,
                {
                    payload,
                }: {
                    payload: {};
                },
            ) => Promise<any>
        >(putRequest, '/user', { payload });

        yield put(userUpdateSuccess(data));
    } catch (e: any) {
        yield call<IFailurePutCall>(failurePut, e, userUpdateFailure);
    }
}

export function* checkPasswordComplexitySaga(password: string) {
    const passwordMaxLength: number = yield select(getPasswordMaxLengthSelector);
    const passwordMinLength: number = yield select(getPasswordMinLengthSelector);
    if (password.length < passwordMinLength) {
        return 'error.password_min_length';
    } else if (password.length > passwordMaxLength) {
        return 'error.password_max_length';
    } else if (!checkIsPasswordValidWithParams(password)) {
        return 'error.password_simple';
    }
    return '';
}

export function* userUpdatePasswordSaga({ payload }: ReturnType<typeof userUpdatePasswordAction>) {
    try {
        if (payload) {
            const passwordComplexityError: string = yield call<(password: string) => any>(
                checkPasswordComplexitySaga,
                payload.passwordNew,
            );
            if (passwordComplexityError) {
                yield put(userUpdatePasswordFailure(passwordComplexityError));
                return;
            }
            if (payload.passwordNew !== payload.passwordNew2) {
                yield put(userUpdatePasswordFailure('promo.fx.new_password.text2'));
                return;
            }
            yield call(putRequest, '/user/password/change', {
                password: payload.password,
                passwordNew: payload.passwordNew,
            });
            payload.callback && payload.callback();
        }
    } catch (e: any) {
        yield call<IFailurePutCall>(failurePut, e, userUpdatePasswordFailure);
    }
}

export function* userUpdateUsernameSaga({ payload }: ReturnType<typeof userUpdateUsernameAction>) {
    try {
        if (payload) {
            const { username } = payload;

            const resp = yield call(putRequest, '/user/username', {
                username
            });

            yield put(userUpdateUsernameSuccess(resp));
        }
    } catch (e) {
        yield call<IFailurePutCall>(failurePut, e, userUpdateUsernameFailure);
    }
}

export function* openModalSaga({ payload }: ReturnType<typeof openModalAction>) {
    try {
        const currentPayload = _.isObject(payload) ? payload : { name: payload };
        appHistoryPushWithSearchParams({
            m: currentPayload.name,
            tab: currentPayload.tab || '',
        });
        yield put(
            appLog({
                type: LOG_EVENT_TYPES.modal,
                payload: {
                    action: MODALS_LOG_ACTION_TYPES.OPEN,
                    ...currentPayload,
                },
            }),
        );
    } catch (e) {
    }
}

export function* closeModalSaga() {
    try {
        const openedModal: IOpenModalPayload = yield select(makeSelectOpenedModalPrevious());
        appHistoryPushWithSearchParams({
            m: null,
            tab: null,
        });
        yield put(
            appLog({
                type: LOG_EVENT_TYPES.modal,
                payload: {
                    action: 'close',
                    ...(_.isObject(openedModal) ? openedModal : {}),
                },
            }),
        );
    } catch (e) {
    }
}

export function* completeOnboardingSaga() {
    try {
        yield call(postRequest, '/user/onboarding/complete');
        yield put(completeOnboardingSuccess());
    } catch (e) {
        yield put(completeOnboardingFailure());
    }
}

export function* userPromocodeApplySaga({ payload }: ReturnType<typeof userPromocodeApplyAction>) {
    try {
        yield call(postRequest, '/user/promocode/apply', payload);
        yield put(userPromocodeApplySuccess());
        yield put(appInitAction());
        yield put(accountGetPortfolioAction());
        yield put(setWelcomeApplyBonusAction(true));
    } catch (e: any) {
        yield call<IFailurePutCall>(failurePut, e, userPromocodeApplyFailure);
    }
}

export function* userPromocodeRemoveSaga({ payload }: ReturnType<typeof userPromocodeRemoveAction>) {
    try {
        yield call(postRequest, '/user/promocode/remove', payload);
        yield put(userPromocodeRemoveSuccess());
        yield put(appInitAction());
        yield put(accountGetPortfolioAction());
    } catch (e: any) {
        yield call<IFailurePutCall>(failurePut, e, userPromocodeRemoveFailure);
    }
}

export function* processUseAbTestQueueSaga() {
    yield call(timeout, 100);
    const abTestsNames = yield select(getAbTestsIsUsedQueueSelector);
    yield put(updateAbTestIsUsedAction(_.uniq(abTestsNames)));
}

export function* updateAbTestsIsUsedSaga({ payload }: ReturnType<typeof updateAbTestIsUsedAction>) {
    yield put(
        sendNotificationMessageAction({
            type: NOTIFICATIONS_MESSAGE_TYPES.AB_TEST,
            params: {
                event: NOTIFICATION_MESSAGE_AB_TESTS_EVENTS.USE_AB_TEST,
                payload,
            },
        }),
    );
}

export function* loadAccountTradeInfoSaga({ payload }: ReturnType<typeof loadAccountTradeInfoAction>) {
    try {
        const data: {
            accountCode: string;
        } = yield call(getRequest, `/account/${encodeURIComponent(payload)}/trade-info`);
        yield put(
            loadAccountTradeInfoSuccess({
                accountCode: data.accountCode,
                data,
            }),
        );
    } catch (e: any) {
        yield call<IFailurePutCall>(failurePut, e, loadAccountTradeInfoFailure);
    }
}

export function* setRecentDoneSaga({ payload }: ReturnType<typeof setRecentDoneAction>) {
    try {
        if (payload) yield call(getRequest, `/user/recent/${encodeURIComponent(payload)}/done`);
    } catch (e) {
    }
}

export function* setUserSelectedAccountCodeSaga({ payload }: ReturnType<typeof setUserSelectedAccountCode>) {
    try {
        localStorage.setItem('selectedAccount', payload);
        yield call(postRequest, `/account/${encodeURIComponent(payload)}/select`);
    } catch (e) {

    }
}

const requestStaticLang = (locale: string) => {
    return new Promise((res, rej) => {
        import(/* webpackChunkName: "locale-[request]" */ `../lang/${locale}.json`).then((data) => {
            res(data?.default);
        });
    });
};

export function* updateLangSaga({ payload }: ReturnType<typeof updateLangAction>) {
    try {
        const availableLocales = yield select(getAvailableLocales);
        const systemLocale = getSystemLocale();
        let newLocale = payload.locale || systemLocale;
        newLocale = _.find(availableLocales, { value: LOCALE_CODES[newLocale] }) ? newLocale : LOCALE_CODES.en;
        const userLocale: string = yield select(getUserLocale);
        const lang = yield select(getAppLangSelector);
        if (lang?.lang !== newLocale || userLocale !== newLocale) {
            const lang = yield call(requestStaticLang, LOCALE_CODES[newLocale]);
            setSystemLocale(LOCALE_CODES[newLocale]);
            yield put(setLangDataAction(lang));
        }
        if (userLocale !== newLocale) {
            yield call(userUpdateSaga, { type: USER_UPDATE_START, payload: { locale: newLocale } });
        }
        if (payload.isRefreshApp) {
            window.location.reload();
        }
    } catch (e: any) {
        console.log('updateLangSaga error ', e);
    }
}

export function* sendRequestFeedbackSaga(props: ReturnType<typeof sendRequestFeedbackAction>) {
    try {
        yield call(postRequest, '/user/feedback', props.payload);
        yield put(sendRequestFeedbackSuccess());
    } catch (e: any) {
        yield call<IFailurePutCall>(failurePut, e, sendRequestFeedbackFailure);
    }
}

export function* isEnabledByFeatureAndAbTestSaga({ payload }: {
    payload: { feature: TFeaturesNames, abTest: TAbTestNames }
}) {
    const { feature, abTest } = payload;
    const features = yield select(getAppFeatures);
    const isEnabledByFeature = features[feature];

    const abTests = yield select(getAbTestsSelector);
    const currentAbTest = abTests[abTest];
    const isAbTestActive = !!currentAbTest;
    const isEnabledByAbTest = !isAbTestActive || !!currentAbTest?.option;

    if (currentAbTest && !currentAbTest.is_used) {
        yield put(addAbTestToIsUsedQueueAction(abTest));
    }

    return isEnabledByFeature && isEnabledByAbTest;
}

export function* deleteProfileSaga(props: ReturnType<typeof deleteProfileAction>) {
    try {
        yield call(postRequest, '/user/delete', { password: props.payload });
        yield put(deleteProfileSuccess());
        yield call(logoutStartSaga, {});
    } catch (e) {
        yield call<IFailurePutCall>(failurePut, e, deleteProfileFailure);
    }
}

export function* sendFacebookEventSaga({ payload }: ReturnType<typeof sendFacebookEventAction>) {
    payload.uid = yield select(getUserUidSelector);
    yield call(sendFacebookEvent, payload);
}

function* logPaymentOpenAppsflyerSaga({ payload }: ReturnType<typeof logPaymentOpenAppsflyerAction>) {
    yield put(logAppsflyerAction({
        name: LOG_AF_EVENT_NAMES.PAYMENT_OPEN,
        payload: {
            source: payload.source,
        },
    }));
}

export function* appSagas() {
    yield takeEvery(APP_INIT_START, appInitStartSaga);
    yield takeEvery(APP_LOG, appLogSaga);
    yield takeEvery(LOG_ACTION, logActionSaga);
    yield takeEvery(LOG_APPSFLYER_ACTION, logAppsflyerSaga);
    yield takeEvery(LOG_PAYMENT_OPEN_APPSFLYER_ACTION, logPaymentOpenAppsflyerSaga);
    yield takeEvery(_ROUTER_LOCATION_CHANGE, locationChangeSaga);
    yield takeEvery(USER_UPDATE_START, userUpdateSaga);
    yield takeEvery(USER_UPDATE_PASSWORD_START, userUpdatePasswordSaga);
    yield takeEvery(USER_UPDATE_USERNAME_START, userUpdateUsernameSaga);
    yield takeEvery(OPEN_MODAL, openModalSaga);
    yield takeEvery(CLOSE_MODAL, closeModalSaga);
    yield takeEvery(COMPLETE_ONBOARDING_START, completeOnboardingSaga);
    yield takeEvery(USER_PROMOCODE_APPLY_START, userPromocodeApplySaga);
    yield takeEvery(USER_PROMOCODE_REMOVE_START, userPromocodeRemoveSaga);
    yield takeEvery(UPDATE_AB_TEST_IS_USED_START, updateAbTestsIsUsedSaga);
    yield takeEvery(LOAD_ACCOUNT_TRADE_INFO_START, loadAccountTradeInfoSaga);
    yield takeEvery(SET_RECENT_DONE, setRecentDoneSaga);
    yield takeEvery(SET_USER_SELECTED_ACCOUNT_CODE, setUserSelectedAccountCodeSaga);
    yield takeEvery(UPDATE_LANG_ACTION, updateLangSaga);
    yield takeEvery(SEND_REQUEST_FEEDBACK_START, sendRequestFeedbackSaga);
    yield takeEvery(DELETE_PROFILE_START, deleteProfileSaga);
    yield takeEvery(SEND_FACEBOOK_EVENT_ACTION, sendFacebookEventSaga);
    yield takeLatest(ADD_AB_TEST_TO_IS_USED_QUEUE, processUseAbTestQueueSaga);
}
